import React, { useEffect, useRef, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import L from 'leaflet';
import 'leaflet-fullscreen';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Reusable map component without draw controls.
 * Props:
 *  - geoJsonData: GeoJSON FeatureCollection
 *  - onFeatureClick: (feature, layer) => void
 *  - styleFunction: (feature) => style object
 *  - center: [lat, lng] default center
 *  - zoom: default zoom level
 *  - height: CSS height (default '100%')
 *  - additionalLayers: array of { name, geoJson, style } for overlay layers
 *  - showLayerControl: boolean
 *  - fitBounds: boolean (default true)
 *  - mapId: unique id for the map div (required for multiple maps)
 */
function MapView({
  geoJsonData,
  onFeatureClick,
  styleFunction,
  center = [39.8283, -98.5795],
  zoom = 4,
  height = '100%',
  additionalLayers = [],
  showLayerControl = true,
  fitBounds = true,
  mapId = 'mapview',
  pointToLayerFn,
}) {
  const mapRef = useRef(null);
  const geoJsonLayerRef = useRef(null);
  const additionalLayersRef = useRef([]);
  const layerControlRef = useRef(null);

  const stableStyleFn = useRef(styleFunction);
  stableStyleFn.current = styleFunction;
  const stableClickFn = useRef(onFeatureClick);
  stableClickFn.current = onFeatureClick;
  const stablePointFn = useRef(pointToLayerFn);
  stablePointFn.current = pointToLayerFn;

  // Initialize map once
  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map(mapId, {
      fullscreenControl: true,
      fullscreenControlOptions: { position: 'topright' },
    }).setView(center, zoom);

    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    });
    const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: '&copy; Esri',
    });
    const topoLayer = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenTopoMap',
    });
    const darkLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; CartoDB',
    });

    osmLayer.addTo(map);

    if (showLayerControl) {
      const baseLayers = {
        'Streets': osmLayer,
        'Satellite': satelliteLayer,
        'Topographic': topoLayer,
        'Dark': darkLayer,
      };
      layerControlRef.current = L.control.layers(baseLayers, null, { position: 'topright' }).addTo(map);
    }

    L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  // Update primary GeoJSON layer
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old layer
    if (geoJsonLayerRef.current) {
      map.removeLayer(geoJsonLayerRef.current);
      geoJsonLayerRef.current = null;
    }

    if (geoJsonData && geoJsonData.features && geoJsonData.features.length > 0) {
      const layerOpts = {
        style: (feature) => stableStyleFn.current ? stableStyleFn.current(feature) : { color: '#6366f1', weight: 3, opacity: 0.8 },
        onEachFeature: (feature, layer) => {
          if (stableClickFn.current) {
            layer.on('click', () => stableClickFn.current(feature, layer));
          }
        },
      };

      if (stablePointFn.current) {
        layerOpts.pointToLayer = (feature, latlng) => stablePointFn.current(feature, latlng);
      }

      const newLayer = L.geoJSON(geoJsonData, layerOpts);
      newLayer.addTo(map);
      geoJsonLayerRef.current = newLayer;

      if (fitBounds) {
        const bounds = newLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40] });
        }
      }
    }
  }, [geoJsonData, fitBounds]);

  // Update additional overlay layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old additional layers
    additionalLayersRef.current.forEach(l => map.removeLayer(l));
    additionalLayersRef.current = [];

    additionalLayers.forEach(({ geoJson, style, pointToLayer: ptl }) => {
      if (!geoJson || !geoJson.features || geoJson.features.length === 0) return;
      const opts = {
        style: () => style || { color: '#ef4444', weight: 2, dashArray: '6 4' },
      };
      if (ptl) opts.pointToLayer = ptl;
      const layer = L.geoJSON(geoJson, opts);
      layer.addTo(map);
      additionalLayersRef.current.push(layer);
    });
  }, [additionalLayers]);

  return <div id={mapId} style={{ height, width: '100%', borderRadius: '8px' }} />;
}

export default MapView;
